import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Crown, Trophy, MapPin, Building2, CheckCircle2, Award, Flame, Sparkles } from "lucide-react";
import SeoMeta from "@/components/SeoMeta";
import { TopPodcastsWidget } from "@/components/PodcastSparkline";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const PERIODS = [
  { id: "week",  label: "This Week" },
  { id: "month", label: "This Month" },
  { id: "all",   label: "All Time" },
];

const KIND_META = {
  creator:  { label: "Creator",  emoji: "👑", color: "from-[#DC2626] to-[#2563EB]" },
  agent:    { label: "Agent",    emoji: "🤝", color: "from-[#10B981] to-[#3B82F6]" },
  business: { label: "Business", emoji: "🏢", color: "from-[#1E40AF] to-[#3B82F6]" },
};

export default function LeaderboardPage() {
  const [period, setPeriod] = useState("month");
  const [city, setCity] = useState("");
  const [appliedCity, setAppliedCity] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({ period });
    if (appliedCity) params.set("city", appliedCity);
    axios.get(`${API}/onlybest/leaderboard?${params.toString()}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ leaders: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period, appliedCity]);

  const top3 = (data?.leaders || []).slice(0, 3);
  const rest = (data?.leaders || []).slice(3);

  return (
    <div className="min-h-screen bg-[#070B16] text-white pt-24 pb-24" data-testid="leaderboard-page">
      <SeoMeta
        title="OnlyBest Leaderboard — Top Local Promoters | LocalBest.ai"
        description="See who's leading the OnlyBest movement — top creators, agents, and businesses promoting the best in their community."
        canonical="https://localbest.ai/leaderboard"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#DC2626]/15 to-[#2563EB]/15 border border-[#DC2626]/30 mb-5">
            <Trophy className="w-3.5 h-3.5 text-[#DC2626]" />
            <span className="text-xs sm:text-sm font-semibold text-[#DC2626]">OnlyBest™ Leaderboard</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-['Outfit'] leading-[1.05] tracking-tight">
            The people <span className="bg-gradient-to-r from-[#DC2626] via-[#2563EB] to-[#1E40AF] bg-clip-text text-transparent">promoting the best</span>.
          </h1>
          <p className="mt-4 text-base sm:text-lg text-[#94A3B8] max-w-2xl mx-auto">
            Updated continuously. Points are earned for shares, clicks, and the leads & customers a participant sends to local businesses.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-10" data-testid="leaderboard-filters">
          <div className="inline-flex rounded-full border border-white/10 bg-white/5 p-1">
            {PERIODS.map((p) => (
              <button
                key={p.id}
                onClick={() => setPeriod(p.id)}
                className={`px-4 py-2 rounded-full text-xs sm:text-sm font-semibold transition ${
                  period === p.id ? "bg-gradient-to-r from-[#DC2626] to-[#2563EB] text-white" : "text-[#94A3B8] hover:text-white"
                }`}
                data-testid={`period-${p.id}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <form
            onSubmit={(e) => { e.preventDefault(); setAppliedCity(city.trim()); }}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-2 py-1"
          >
            <MapPin className="w-4 h-4 text-[#94A3B8] ml-1" />
            <input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Filter by city"
              className="bg-transparent text-sm text-white placeholder:text-[#475569] focus:outline-none px-1 py-1.5 w-36"
              data-testid="leaderboard-city-input"
            />
            <button type="submit" className="text-xs px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white" data-testid="leaderboard-city-apply">Go</button>
          </form>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-3 gap-4">
            {[0, 1, 2].map((i) => <div key={i} className="h-48 rounded-2xl bg-white/5 animate-pulse" />)}
          </div>
        ) : (
          <>
            {/* Top 3 podium */}
            {top3.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8" data-testid="leaderboard-podium">
                {top3.map((l, i) => <PodiumCard key={l.creator_slug} leader={l} place={i + 1} />)}
              </div>
            )}

            {/* Rest of list */}
            {rest.length > 0 && (
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] divide-y divide-white/5 overflow-hidden" data-testid="leaderboard-rows">
                {rest.map((l) => <LeaderRow key={l.creator_slug} leader={l} />)}
              </div>
            )}

            {top3.length === 0 && (
              <div className="text-center py-20" data-testid="leaderboard-empty">
                <Sparkles className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" />
                <p className="text-white font-medium">No leaders yet for this period.</p>
                <p className="text-[#94A3B8] text-sm mt-1">Be the first — turn on your OnlyBest Campaign and start sharing your tracked links.</p>
                <Link to="/onlybest" className="inline-flex mt-5 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#DC2626] to-[#2563EB] text-white text-sm font-semibold">
                  Join the movement
                </Link>
              </div>
            )}

            {/* Top podcasts widget — shown only on All-Time view to reward audio-first creators */}
            {period === "all" && (
              <div className="mt-10">
                <TopPodcastsWidget period="all" limit={5} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function PodiumCard({ leader, place }) {
  const meta = KIND_META[leader.kind] || KIND_META.business;
  const podium = {
    1: { border: "border-[#DC2626]/60", glow: "shadow-[#DC2626]/30", placeBg: "from-[#DC2626] to-[#2563EB]", trophy: "🥇" },
    2: { border: "border-white/30",     glow: "shadow-white/10",    placeBg: "from-[#94A3B8] to-[#64748B]", trophy: "🥈" },
    3: { border: "border-[#D97706]/40", glow: "shadow-[#D97706]/20", placeBg: "from-[#D97706] to-[#7C2D12]", trophy: "🥉" },
  }[place];
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: place * 0.05 }}
      className={`relative rounded-3xl border ${podium.border} bg-gradient-to-br from-[#0F172A] to-[#070B16] p-5 shadow-2xl ${podium.glow}`}
    >
      <div className={`absolute -top-3 -left-3 w-12 h-12 rounded-2xl bg-gradient-to-br ${podium.placeBg} flex items-center justify-center text-xl shadow-lg`}>
        {podium.trophy}
      </div>
      <Link to={`/business/${leader.creator_slug}`} className="flex items-center gap-4 mb-4 min-w-0">
        {leader.logo_url ? (
          <img src={leader.logo_url} alt={leader.name} className="w-16 h-16 rounded-2xl object-cover bg-white/10 flex-shrink-0" />
        ) : (
          <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center flex-shrink-0">
            <Crown className="w-7 h-7 text-[#DC2626]" />
          </div>
        )}
        <div className="min-w-0">
          <p className="text-white font-bold truncate">{leader.name}</p>
          <p className="text-xs text-[#94A3B8] truncate flex items-center gap-1.5">
            <span>{meta.emoji} {meta.label}</span>
            {leader.is_verified && <CheckCircle2 className="w-3.5 h-3.5 text-[#10B981]" aria-label="Verified" />}
          </p>
          {(leader.city || leader.state) && (
            <p className="text-[10px] text-[#64748B] truncate flex items-center gap-1 mt-0.5">
              <MapPin className="w-3 h-3" /> {[leader.city, leader.state].filter(Boolean).join(", ")}
            </p>
          )}
        </div>
      </Link>
      <div className="grid grid-cols-3 gap-2 text-center">
        <Stat label="Points" value={leader.points} highlight />
        <Stat label="Clicks" value={leader.clicks + leader.views} />
        <Stat label="Leads"  value={leader.conversions} />
      </div>
      <p className="mt-3 text-xs text-[#94A3B8] text-center">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 border border-white/10">
          <span>{leader.level.emoji}</span> {leader.level.name}
        </span>
      </p>
    </motion.div>
  );
}

function LeaderRow({ leader }) {
  const meta = KIND_META[leader.kind] || KIND_META.business;
  return (
    <Link
      to={`/business/${leader.creator_slug}`}
      className="grid grid-cols-[auto_1fr_auto] gap-3 sm:gap-5 items-center px-4 sm:px-6 py-3 hover:bg-white/5 transition min-w-0"
      data-testid={`leaderboard-row-${leader.creator_slug}`}
    >
      <span className="text-sm font-mono text-[#64748B] w-8 text-right">#{leader.rank}</span>
      <div className="flex items-center gap-3 min-w-0">
        {leader.logo_url ? (
          <img src={leader.logo_url} alt={leader.name} className="w-10 h-10 rounded-xl object-cover bg-white/10 flex-shrink-0" />
        ) : (
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
            <Crown className="w-5 h-5 text-[#DC2626]" />
          </div>
        )}
        <div className="min-w-0">
          <p className="text-white font-semibold truncate flex items-center gap-1.5">
            {leader.name}
            {leader.is_verified && <CheckCircle2 className="w-3.5 h-3.5 text-[#10B981]" />}
            {leader.is_best_of_best && <Award className="w-3.5 h-3.5 text-[#DC2626]" />}
          </p>
          <p className="text-[11px] text-[#64748B] truncate">
            {meta.emoji} {meta.label}{(leader.city || leader.state) ? ` · ${[leader.city, leader.state].filter(Boolean).join(", ")}` : ""}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3 sm:gap-5 text-xs sm:text-sm">
        <span className="hidden sm:inline text-[#94A3B8]">{leader.level.emoji} {leader.level.name}</span>
        <span className="font-bold text-white tabular-nums">{leader.points.toLocaleString()}</span>
        <Flame className="w-4 h-4 text-[#DC2626]" />
      </div>
    </Link>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`rounded-xl p-2 ${highlight ? "bg-gradient-to-br from-[#DC2626]/15 to-[#2563EB]/10" : "bg-white/[0.03]"}`}>
      <p className={`text-base sm:text-lg font-bold tabular-nums ${highlight ? "text-[#DC2626]" : "text-white"}`}>{(value ?? 0).toLocaleString()}</p>
      <p className="text-[10px] text-[#94A3B8] uppercase tracking-wider">{label}</p>
    </div>
  );
}
